import {getProjectStages} from './ProjectStageCache';
import {StageProfile, stageLabel} from './projectStageUtil';

type StageIntl = Parameters<typeof stageLabel>[1];

/**
 * The input fields that move a project to the first stage with the given profile: the profile as `status`. The
 * server lands it in the company's first live stage with that profile, so a stage id cached at app start that has
 * since been deleted can't make the save fail.
 */
export const stageChangeInput = (profile: StageProfile): {status: StageProfile} => ({status: profile});

/**
 * The `Status` to track for a change to the stage with this id: its profile for a default stage, its name for a stage
 * without one, undefined when the stage is not cached.
 */
export const stageTrackingStatus = (stageId: string): string | undefined => {
	const stage = getProjectStages().find(candidate => candidate.id === stageId);
	return stage?.profile ?? stage?.name;
};

export type StagePickerOption = {value: string; label: string; profile: StageProfile | string | null};

/** A company stage as the pickers receive it, from the stage cache or from a Relay fragment. */
type PickerStage = {id: string; name: string | null; sortOrder: number | null; profile: StageProfile | string | null};

const DEFAULT_PROFILE_ORDER: StageProfile[] = ['OPPORTUNITY', 'PLANNING', 'RUNNING', 'HALTED', 'DONE'];

/**
 * The stage picker's options, `value` being the stage id. With custom stages on: every company stage in admin order.
 * Off: one stage per default profile (the first by admin order), in the usual order. Opportunity stages are left out
 * unless the user has opportunity access or the project is already in that stage.
 */
export const stagePickerOptions = (
	stages: ReadonlyArray<PickerStage>,
	opts: {customStagesOn: boolean; showOpportunity: boolean; currentStageId: string | null},
	intl: StageIntl
): StagePickerOption[] => {
	const bySortOrder = [...stages].sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
	const visible = bySortOrder.filter(
		stage => stage.profile !== 'OPPORTUNITY' || opts.showOpportunity || stage.id === opts.currentStageId
	);
	const chosen = opts.customStagesOn
		? visible
		: DEFAULT_PROFILE_ORDER.flatMap(profile => {
				const stage = visible.find(candidate => candidate.profile === profile);
				return stage ? [stage] : [];
		  });
	return chosen.map(stage => ({value: stage.id, label: stageLabel(stage, intl), profile: stage.profile}));
};

/**
 * The option showing the project's current stage: the option with its id, else (a profile that shares its option with
 * another stage when custom stages are off) the option with its profile.
 */
export const selectedStageOption = <T extends {value: string; profile: StageProfile | string | null}>(
	options: T[],
	stage: {id?: string | null; profile?: StageProfile | string | null} | null | undefined
): T | undefined => {
	if (!stage) {
		return undefined;
	}
	return (
		options.find(option => option.value === stage.id) ??
		(stage.profile ? options.find(option => option.profile === stage.profile) : undefined)
	);
};
